import { useEffect, useMemo, useState } from "react";
import classNames from "classnames";

import person from "@/assets/icons/person.svg";
import humanBlack from "@/assets/img/bmiData/human-black.svg";
import linesBlack from "@/assets/img/bmiData/lines-black.svg";
import triangleBlack from "@/assets/img/bmiData/triangle-black.svg";
import StickyButton from "@/common/StickyButton";
import { logAnalyticsEvent } from "@/constants/amplitude";
import { ActivityLevelAnswersLabels } from "@/constants/answersLabels";
import {
    BMI_STAGES_ARR,
    bmiStagesLabels,
    extraUnderweight,
    getBMI,
    maxNormal,
    maxObesity,
    maxUnderweight,
    normal,
} from "@/constants/bmiInfo";
import { WebEventProps, WebEventTypes } from "@/constants/event";
import { FitnessGoals, goalTextById } from "@/constants/fitnessGoal";
import { FitnessLevels, getFitnessLevelLabel } from "@/constants/fitnessLevels";
import { QUESTIONS_IDS } from "@/constants/questionsIds";
import femaleTondedBody from "@/female/assets/bmi-screen/female-toned-body.png";
import i18n from "@/localization/i18next";
import { QuestionComponentProps } from "@/types/common";
import { useGender } from "@/utils/GenderProvider";
import useAnswers from "@/utils/useAnswers";

const BmiScreen = ({ handleNext }: QuestionComponentProps) => {
    const [left, setLeft] = useState(0);

    const { answers, getAnswersValue } = useAnswers();
    const { gender } = useGender();

    const handleProceed = () => {
        logAnalyticsEvent(WebEventTypes.WEB_BUTTON_CLICK, {
            [WebEventProps.BUTTON_NAME]: "proceed",
            [WebEventProps.SCREEN]: "female_bmi_results",
        });
        logAnalyticsEvent(WebEventTypes.RESULT_EVENT, {
            [WebEventProps.SCREEN]: "female_bmi_results",
        });

        handleNext();
    };

    const height = answers.height as number;
    const weight = answers.current_weight as number;
    const fitnessLevel = getFitnessLevelLabel(answers.fitness_level as FitnessLevels, gender);
    const activityLevel = getAnswersValue(QUESTIONS_IDS.ACTIVITY_LEVEL);
    const goalText = goalTextById[answers[QUESTIONS_IDS.PRIMARY_GOAL] as FitnessGoals];

    const BMI = getBMI(weight, height);
    const leftTranslate = useMemo(() => {
        if (BMI < extraUnderweight) {
            return 5;
        }
        if (BMI < maxUnderweight) {
            return 30;
        }
        if (BMI < normal) {
            return 40;
        }
        if (BMI < maxNormal) {
            return 50;
        }
        if (BMI < maxObesity) {
            return 70;
        }
        if (BMI >= maxObesity) {
            return 85;
        }
        return 0;
    }, [BMI]);

    useEffect(() => {
        setTimeout(() => {
            setLeft(leftTranslate);
        }, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getTextByBmi = (bmi: number) => {
        if (bmi < maxUnderweight) {
            return "underweight";
        } else if (bmi < maxNormal) {
            return "normal";
        } else return "overweight";
    };

    const getBmiStages = () => {
        const bmiStage = getTextByBmi(BMI);

        return BMI_STAGES_ARR.map((stage, i) => (
            <div
                key={i}
                className={classNames("font-semibold uppercase", {
                    "text-zing-neutral-cool-grey-600": stage !== bmiStage,
                    "animate-[textTransparentToBlack_5s_ease-in-out_forwards]": stage === bmiStage,
                })}
            >
                {bmiStagesLabels[stage]}
            </div>
        ));
    };

    return (
        <div className="grid h-full content-between gap-4">
            <div className="grid gap-4">
                <div className="text-zing-28-26 font-semibold leading-[40px] text-black">
                    {i18n.t("your_fitness_level")}
                </div>
                <div className="flex h-[130px] flex-col justify-between rounded-2xl bg-zing-neutral-cool-grey-200 pt-3">
                    <div className="flex justify-between px-6 text-zing-12-18 font-semibold text-black">
                        <div>{i18n.t("bmi_message")}</div>
                        <div className="capitalize text-zing-neutral-cool-grey-600">
                            {i18n.t("normal_bmi")}
                        </div>
                    </div>
                    <div className="relative mx-6 mt-6">
                        <div className="bmi-range h-2 rounded-2xl" />
                        <div
                            className="absolute -top-2 h-6 w-1 rounded bg-black transition-all duration-[5000ms]"
                            style={{ left: `${left}%` }}
                        ></div>
                        <span
                            className="tooltiptext absolute -top-[450%] w-fit whitespace-nowrap rounded-2xl bg-black px-[7px] text-zing-10-18 text-white transition-all duration-[5000ms]"
                            style={{ left: `${left}%`, transform: `translateX(-45%)` }}
                        >
                            {i18n.t("you_bmi", { bmi: BMI })}
                        </span>
                    </div>
                    <div className="mb-2 flex justify-between px-7 text-zing-10-18">
                        {getBmiStages()}
                    </div>
                </div>
                <div className="relative grid grid-flow-col rounded-2xl bg-zing-neutral-cool-grey-200 md:flex md:h-[200px] md:justify-between">
                    <div className="grid w-[55%] gap-2 p-6 pr-0">
                        <div className="flex items-center gap-2 pb-2">
                            <img src={humanBlack} alt="" className="h-6 w-6" />
                            <div className="grid">
                                <div className="text-zing-12-18 text-zing-neutral-cool-grey-600">
                                    {i18n.t("lifestyle_prompt")}
                                </div>
                                <div className="animate-[textGrey200ToBlack_500ms_ease-in-out_5000ms_forwards] leading-[20px] text-zing-neutral-cool-grey-200">
                                    {ActivityLevelAnswersLabels[activityLevel]}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 pb-2">
                            <img src={triangleBlack} alt="" className="h-8 w-6" />
                            <div className="grid">
                                <div className="text-zing-12-18 text-zing-neutral-cool-grey-600">
                                    {i18n.t("exercise_prompt")}
                                </div>
                                <div className="animate-[textGrey200ToBlack_500ms_ease-in-out_5500ms_forwards] leading-[20px] text-zing-neutral-cool-grey-200">
                                    {goalText}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <img src={linesBlack} alt="" className="h-6 w-6" />
                            <div className="grid">
                                <div className="text-zing-12-18 text-zing-neutral-cool-grey-600">
                                    {i18n.t("activity_level")}
                                </div>
                                <div className="animate-[textGrey200ToBlack_500ms_ease-in-out_6000ms_forwards] capitalize leading-[20px] text-zing-neutral-cool-grey-200">
                                    {fitnessLevel}
                                </div>
                            </div>
                        </div>
                    </div>
                    <img
                        src={femaleTondedBody}
                        alt=""
                        className="absolute -right-[10px] max-h-full"
                    />
                </div>
                <div className="grid animate-[appear_500ms_cubic-bezier(0.4,_0,_0.2,_1)_6500ms_forwards] items-start gap-2 rounded-2xl bg-zing-warm-tangerine-200 p-4 text-black opacity-0">
                    <div className="flex items-center gap-2">
                        <img src={person} alt="" />
                        <span className="text-zing-12-18 font-semibold">
                            {i18n.t("good_starting_bmi", { goal: goalText?.toLowerCase() })}
                        </span>
                    </div>
                    <span className="text-zing-12-18">{i18n.t("create_program_message")}</span>
                </div>
            </div>
            <StickyButton
                onClick={handleProceed}
                className="animate-[appear_1000ms_cubic-bezier(0.4,_0,_0.2,_1)_7000ms_forwards] opacity-0"
            />
        </div>
    );
};

export default BmiScreen;
